import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  checkPaymentLinkingStatus,
  selectPaymentStatus,
  selectTransaction,
} from "../../redux/slices/paymentLinkingSlice";
import { formatCurrency } from "../utils/formatters";
import {
  CheckCircle,
  XCircle,
  Clock,
  Copy,
  ArrowRight,
  Smartphone,
  Calendar,
  RefreshCw,
} from "lucide-react";
import usePaymentStatusChecker from "../hooks/usePaymentStatusChecker";

const PaymentResult = () => {
  const { linkCode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { transaction, paymentStatus, paymentLink } = useSelector((state) => ({
    transaction: state.paymentLinking.transaction,
    paymentStatus: state.paymentLinking.paymentStatus,
    paymentLink: state.paymentLinking.paymentLink,
  }));

  const [copied, setCopied] = useState(false);

  const searchParams = new URLSearchParams(location.search);

  // Get transaction ID: location state → Redux → URL param (survives page reload)
  const transactionId = location.state?.transactionId || transaction?.transaction_id || transaction?.initial_payment?.transaction_id || searchParams.get('transaction_id');

  // GoPay tokenization returns the customer here without a
  // transaction (the first charge is fired server-side after the
  // pay-account-notify webhook). Hand control back to the
  // GoPayLinking page so its saved-methods polling can take over
  // and surface the "linking…" / "linked!" state.
  const isFromGoPay = searchParams.get("from_gopay") === "1";

  useEffect(() => {
    if (isFromGoPay && !transactionId) {
      navigate(`/b2b/linking/${linkCode}/gopay?from_gopay=1`, { replace: true });
    }
  }, [isFromGoPay, transactionId, linkCode, navigate]);

  // ShopeePay bounces the customer's browser back here (their
  // redirectUrl) with authCode + partnerReferenceNo + result as
  // query params after account-linking authorization. Unlike GoPay,
  // ShopeePay's linking completes SYNCHRONOUSLY — ShopeePayLinking
  // needs the full query string (not just a flag) to call
  // completeShopeepayLinking, so the whole search string is forwarded
  // as-is rather than rebuilt with only from_shopeepay=1.
  const isFromShopeepay = searchParams.get("from_shopeepay") === "1";

  useEffect(() => {
    if (isFromShopeepay && !transactionId) {
      navigate(`/b2b/linking/${linkCode}/shopeepay${location.search}`, {
        replace: true,
      });
    }
  }, [isFromShopeepay, transactionId, linkCode, navigate, location.search]);

  // Normalize recurring_setup shape into flat transaction format
  const normalizeTransaction = (txn) => {
    if (!txn) return null;
    let normalized = txn;
    if (txn.status === 'recurring_setup' && txn.initial_payment) {
      normalized = {
        ...txn,
        transaction_id: txn.initial_payment.transaction_id,
        amount: txn.initial_payment.amount,
        status: txn.initial_payment.status,
        payment_method: 'credit_card',
      };
    }
    // Process response uses transaction_time; result page expects completed_at
    if (normalized.payment_details?.transaction_time && !normalized.payment_details?.completed_at) {
      normalized = {
        ...normalized,
        payment_details: {
          ...normalized.payment_details,
          completed_at: normalized.payment_details.transaction_time,
        },
      };
    }
    return normalized;
  };

  // Initial transaction from navigate state (has card_masked, auth_code)
  const initialTxn = normalizeTransaction(location.state?.transaction || transaction);

  // Status from location.state on first render; updated by polling after that
  const [currentStatus, setCurrentStatus] = useState(() =>
    location.state?.initialStatus || initialTxn?.status || "pending"
  );

  useEffect(() => {
    if (
      !transaction &&
      !paymentStatus &&
      !transactionId &&
      !isFromGoPay &&
      !isFromShopeepay
    ) {
      navigate(`/b2b/linking/${linkCode}`);
    }
  }, [
    transaction,
    paymentStatus,
    transactionId,
    linkCode,
    navigate,
    isFromGoPay,
    isFromShopeepay,
  ]);

  useEffect(() => {
    // Update status only from polling results (not stale Redux transaction)
    if (paymentStatus?.status) {
      setCurrentStatus(paymentStatus.status);
    }
  }, [paymentStatus]);

  // Poll for status updates if pending
  usePaymentStatusChecker(
    linkCode,
    transactionId,
    currentStatus === "pending" || currentStatus === "processing",
    3000,
    (status) => {
      if (status && status !== "error") {
        setCurrentStatus(status);
      }
    }
  );

  useEffect(() => {
    // Redirect after successful payment
    if (currentStatus === "success" && paymentLink?.settings?.redirect_url) {
      const timer = setTimeout(() => {
        window.location.href = paymentLink.settings.redirect_url;
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [currentStatus, paymentLink]);

  const getStatusConfig = () => {
    switch (currentStatus) {
      case "success":
        return {
          icon: CheckCircle,
          iconStyle: { color: "#01793B" },
          bgStyle: { backgroundColor: "#ECFDF5" },
          title: "Payment Successful!",
          message: "Your payment has been processed successfully.",
          showDetails: true,
        };
      case "failed":
        return {
          icon: XCircle,
          iconStyle: { color: "#DC2626" },
          bgStyle: { backgroundColor: "#FEF2F2" },
          title: "Payment Failed",
          message: "Unfortunately, your payment could not be processed.",
          showDetails: true,
        };
      case "pending":
      case "processing":
      default:
        return {
          icon: Clock,
          iconStyle: { color: "#CA8A04" },
          bgStyle: { backgroundColor: "#FEFCE8" },
          title: "Payment Processing",
          message: "Your payment is being processed. Please wait...",
          showDetails: false,
        };
    }
  };

  const handleCopyTransactionId = () => {
    if (transactionId) {
      navigator.clipboard.writeText(transactionId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleRefreshStatus = async () => {
    try {
      const result = await dispatch(checkPaymentLinkingStatus({ linkCode, transactionId })).unwrap();
      setCurrentStatus(result.status);
    } catch (error) {
      console.error("Failed to refresh status:", error);

      // Show user-friendly error
      const errorMessage = error?.message || error?.data?.message || "Failed to refresh payment status";
      alert(errorMessage); // Simple alert, can be replaced with toast notification
    }
  };

  if (!transaction && !paymentStatus && !transactionId) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: "#F9FAFC" }}>
        <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: "#01793B", borderRightColor: "transparent" }} />
      </div>
    );
  }

  const config = getStatusConfig();
  const Icon = config.icon;

  // Merge: status check has net_amount + completed_at; initial response has card_masked + auth_code
  const displayData = paymentStatus
    ? {
        ...paymentStatus,
        payment_details: {
          ...paymentStatus.payment_details,
          card_masked: initialTxn?.payment_details?.card_masked,
          auth_code: initialTxn?.payment_details?.auth_code,
        },
      }
    : initialTxn;

  return (
    <div className="min-h-screen py-8 sm:py-12 px-4" style={{ backgroundColor: "#F9FAFC" }}>
      <div className="max-w-2xl mx-auto">
        {/* Status Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6 text-center">
          {/* Icon */}
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={config.bgStyle}>
            <Icon className="w-10 h-10" style={config.iconStyle} />
          </div>

          {/* Title & Message */}
          <h1 className="text-lg sm:text-xl font-bold mb-2" style={{ color: "#3B3B3B" }}>{config.title}</h1>
          <p className="text-xs sm:text-sm mb-6" style={{ color: "#515164" }}>{config.message}</p>

          {/* Redirect Notice */}
          {currentStatus === "success" && paymentLink?.settings?.redirect_url && (
            <div className="p-4 rounded-xl mb-6" style={{ backgroundColor: "#F0FDF4" }}>
              <p className="text-xs sm:text-sm" style={{ color: "#166534" }}>
                You will be redirected to the merchant's website in a few seconds...
              </p>
            </div>
          )}

          {/* Transaction ID */}
          {transactionId && (
            <div className="inline-flex items-center rounded-xl px-4 py-2 mb-6" style={{ backgroundColor: "#F3F4F6" }}>
              <span className="text-xs sm:text-sm mr-2" style={{ color: "#515164" }}>Transaction ID:</span>
              <span className="font-mono text-xs sm:text-sm font-semibold" style={{ color: "#3B3B3B" }}>{transactionId}</span>
              <button
                onClick={handleCopyTransactionId}
                className="ml-2 p-1 hover:bg-gray-200 rounded-lg transition-colors"
                title="Copy"
              >
                {copied ? (
                  <CheckCircle className="w-4 h-4" style={{ color: "#01793B" }} />
                ) : (
                  <Copy className="w-4 h-4" style={{ color: "#515164" }} />
                )}
              </button>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {currentStatus === "failed" && (
              <>
                <button
                  onClick={() => navigate(`/b2b/linking/${linkCode}/payment-method`)}
                  className="text-white font-semibold py-3 px-6 rounded-xl transition-all flex items-center justify-center"
                  style={{ backgroundColor: "#01793B" }}
                >
                  Try Again
                  <RefreshCw className="w-5 h-5 ml-2" />
                </button>
                <button
                  onClick={() => navigate(`/b2b/linking/${linkCode}`)}
                  className="bg-gray-100 hover:bg-gray-200 font-semibold py-3 px-6 rounded-xl transition-all flex items-center justify-center"
                  style={{ color: "#515164" }}
                >
                  Back to Details
                  <ArrowRight className="w-5 h-5 ml-2" />
                </button>
              </>
            )}

            {(currentStatus === "pending" || currentStatus === "processing") && (
              <button
                onClick={handleRefreshStatus}
                className="text-white font-semibold py-3 px-6 rounded-xl transition-all flex items-center justify-center"
                style={{ backgroundColor: "#01793B" }}
              >
                Refresh Status
                <RefreshCw className="w-5 h-5 ml-2" />
              </button>
            )}

            {currentStatus === "success" && paymentLink?.settings?.redirect_url && (
              <button
                onClick={() => { window.location.href = paymentLink.settings.redirect_url; }}
                className="text-white font-semibold py-3 px-6 rounded-xl transition-all flex items-center justify-center"
                style={{ backgroundColor: "#01793B" }}
              >
                Go to Merchant
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>
            )}
          </div>
        </div>

        {/* Payment Details Card */}
        {config.showDetails && displayData && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-sm sm:text-base font-semibold mb-4" style={{ color: "#3B3B3B" }}>Payment Details</h2>

            <div className="space-y-4">
              {/* Amount */}
              <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                <span className="text-xs sm:text-sm" style={{ color: "#515164" }}>Amount</span>
                <span className="text-lg sm:text-xl font-bold" style={{ color: "#3B3B3B" }}>
                  {formatCurrency(displayData.amount)}
                </span>
              </div>

              {/* Payment Method */}
              {displayData.payment_method && (
                <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                  <div className="flex items-center">
                    <Smartphone className="w-5 h-5 mr-2" style={{ color: "#515164" }} />
                    <span className="text-xs sm:text-sm" style={{ color: "#515164" }}>Payment Method</span>
                  </div>
                  <span className="text-xs sm:text-sm font-semibold capitalize" style={{ color: "#3B3B3B" }}>
                    {displayData.payment_method.replace("_", " ")}
                  </span>
                </div>
              )}

              {/* Card Details */}
              {displayData.payment_details?.card_masked && (
                <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                  <span className="text-xs sm:text-sm" style={{ color: "#515164" }}>Card</span>
                  <span className="text-xs sm:text-sm font-semibold" style={{ color: "#3B3B3B" }}>
                    {displayData.payment_details.card_masked}
                  </span>
                </div>
              )}

              {/* Authorization Code */}
              {displayData.payment_details?.auth_code && (
                <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                  <span className="text-xs sm:text-sm" style={{ color: "#515164" }}>Auth Code</span>
                  <span className="text-xs sm:text-sm font-semibold" style={{ color: "#3B3B3B" }}>
                    {displayData.payment_details.auth_code}
                  </span>
                </div>
              )}

              {/* Transaction Time */}
              {displayData.payment_details?.completed_at && (
                <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                  <div className="flex items-center">
                    <Calendar className="w-5 h-5 mr-2" style={{ color: "#515164" }} />
                    <span className="text-xs sm:text-sm" style={{ color: "#515164" }}>Completed At</span>
                  </div>
                  <span className="text-xs sm:text-sm font-semibold" style={{ color: "#3B3B3B" }}>
                    {new Date(displayData.payment_details.completed_at).toLocaleString("id-ID")}
                  </span>
                </div>
              )}

              {/* Failure Reason */}
              {displayData.payment_details?.failure_reason && (
                <div className="p-4 rounded-xl" style={{ backgroundColor: "#FEF2F2" }}>
                  <p className="text-xs sm:text-sm font-medium" style={{ color: "#991B1B" }}>Failure Reason</p>
                  <p className="text-xs sm:text-sm mt-1" style={{ color: "#DC2626" }}>
                    {displayData.payment_details.failure_reason}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Pending Notice */}
        {currentStatus === "pending" && (
          <div className="rounded-xl p-4 mb-6" style={{ backgroundColor: "#FEFCE8", border: "1px solid #FDE047" }}>
            <div className="flex items-start">
              <Clock className="w-5 h-5 mr-3 mt-0.5" style={{ color: "#CA8A04" }} />
              <div>
                <p className="text-xs sm:text-sm font-medium" style={{ color: "#854D0E" }}>Payment in Progress</p>
                <p className="text-xs sm:text-sm mt-1" style={{ color: "#A16207" }}>
                  Your payment is being processed. This may take a few moments. You can refresh the status
                  or check back later.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Security Note */}
        <div className="text-center mt-6">
          <p className="text-[10px] sm:text-xs" style={{ color: "#828282" }}>
            For questions about this payment, please contact the merchant directly.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentResult;
